/**
 * ESCHER FALLS — ステージの分類・解放・進行。METAGAME-SPEC §3。
 *
 * **純ロジックのみ。** DOM も three.js も localStorage も触らない。
 * 記録の読み書きは `records.ts`、描画は `ui/stageSelect.ts` の担当。
 */

import type { Level } from '../core/types';
import { isLevelUnlocked, totalBestTime, type RecordsData } from './records';
import { recordIdFor } from './loop2';

/** ステージの区分。 */
export type LevelCategory = 'main' | 'extra';

/**
 * id からステージ区分を決める。
 *
 * - 数字で始まる（`01-first`）→ 本編
 *  * - それ以外（`x1-...`）→ エクストラ
 *
 * レベル JSON に区分を書かせるより id の命名規約で決める方が、
 * 「登録し忘れて本編扱いになる」事故が起きない。
 */
export function categoryOf(levelId: string): LevelCategory {
  return /^\d/.test(levelId) ? 'main' : 'extra';
}

/** ステージ選択画面に出す1件ぶんの情報。 */
export interface StageEntry {
  readonly id: string;
  readonly name: string;
  /** 区分内での通し番号。1 始まり。表示する漢数字はこれを使う。 */
  readonly number: number;
  readonly category: LevelCategory;
  /** 選べるか。 */
  readonly unlocked: boolean;
  /** 一度でもクリアしたか。 */
  readonly cleared: boolean;
  readonly bestTimeMs: number | null;
  readonly bestMoves: number | null;
  /** `LEVELS` 配列での添字。`loadLevelAt` にそのまま渡せる。 */
  readonly levelIndex: number;
  /** エクストラ後半（逆流＋レバー）か。ステージ選択が段を分けるのに使う。 */
  readonly isExtraTier2: boolean;
  /** 購入していなくても遊べる試遊の面か。ステージ選択が印を付ける。 */
  readonly isFreeTrial: boolean;
}

/** 本編ステージの id を、並び順のまま取り出す。 */
export function mainLevelIds(levels: readonly Level[]): string[] {
  return levels.filter((lv) => categoryOf(lv.id) === 'main').map((lv) => lv.id);
}

/** エクストラステージの id を、並び順のまま取り出す。 */
export function extraLevelIds(levels: readonly Level[]): string[] {
  return levels.filter((lv) => categoryOf(lv.id) === 'extra').map((lv) => lv.id);
}

/**
 * エクストラの前半（其の一）の面数。ここを全部通すと後半（其の二）が**まとめて**開く。
 *
 * 前半は逆流だけ、後半は逆流＋レバー、と要求が一段上がるので、
 * 1面ずつ小出しにせず区切りとして開く。
 */
export const EXTRA_TIER1_COUNT = 10;

/**
 * 本編第一章の面数。ここを通すと**エクストラの1面目が無料で開く。**
 *
 * ## なぜ1面だけ配るのか
 * エクストラは購入するまで全部伏せてあった。**中身を一度も見せずに
 * ¥800 を求めていた**ことになる。逆流（水が滝を昇る）はこの作品で
 * いちばん強い驚きなのに、買った後にしか出会えない。
 *
 * ## なぜ全通水ではなく第一章なのか
 * 全通水まで辿り着く人は入れた人の一部でしかない。**そこに全部の期待を
 * かけると母数が小さすぎる。** 第一章なら本編がまだ残っているうちに
 * 逆流を見せられ、「あと19面ある」が遊んでいる間ずっと効く。
 */
export const CHAPTER1_COUNT = 12;

/** 本編第一章を通したか。エクストラの試遊が開く条件。 */
export function isChapter1Complete(
  levels: readonly Level[],
  records: RecordsData,
): boolean {
  const ids = mainLevelIds(levels).slice(0, CHAPTER1_COUNT);
  return ids.length > 0 && ids.every((id) => records.levels[id] !== undefined);
}

/** 無料で試せるエクストラの id。無ければ null。 */
export function freeExtraId(levels: readonly Level[]): string | null {
  return extraLevelIds(levels)[0] ?? null;
}

/** エクストラ後半が解放されているか（前半を全部クリアしたか）。 */
export function isExtraTier2Open(
  levels: readonly Level[],
  records: RecordsData,
): boolean {
  const tier1 = extraLevelIds(levels).slice(0, EXTRA_TIER1_COUNT);
  return tier1.length > 0 && tier1.every((id) => records.levels[id] !== undefined);
}

export interface StageListOptions {
  /**
   * 何周目の一覧か。2 以上だと
   *   - 記録は `<id>#2` のほうを引く（一周目のタイムと混ぜない）
   *   - 本編は**全部選べる**（一周目を通した人しか来られないので、順に開く意味がない）
   */
  loop?: number;
  /**
   * 解放状態を無視して全ステージを選べるようにする（`?unlock=1`）。
   * **テスト・動作確認専用。** 記録には一切書き込まないので、
   * URL から外せば本来の進行状況に戻る。
   */
  readonly unlockAll?: boolean;
  /**
   * 体験版の上限。本編のこの面数まで遊べ、それより先とエクストラは閉じる。
   * **アプリでは渡さない**（`game/demo.ts`）。
   */
  readonly demoLimit?: number;
}

/**
 * ステージ選択画面に出す一覧を組む。
 *
 * 解放の規則（METAGAME-SPEC §3）:
 * - 本編は「直前の面をクリアしていれば選べる」。1面目は常に選べる。
 * - **一度クリアした面は永続的に選べる。** タイムを縮めに戻れるようにするため。
 * - **エクストラは購入するまで閉じている**（かつては本編全通水で開いた）。
 *   全通水の褒美は二周目の解放へ付け替えてある。
 */
export function buildStageList(
  levels: readonly Level[],
  records: RecordsData,
  options: StageListOptions = {},
): StageEntry[] {
  const mainIds = mainLevelIds(levels);
  const extraIds = extraLevelIds(levels);
  const tier1Ids = extraIds.slice(0, EXTRA_TIER1_COUNT);
  const tier2Ids = extraIds.slice(EXTRA_TIER1_COUNT);
  // 購入で開く。`unlockedExtra` は課金に切り替える前に開けていた人のための旗で、
  // **新しく立つことはないが、取り上げもしない。**
  const extraOpen = records.progress.extrasOwned || records.progress.unlockedExtra;
  const tier2Open = isExtraTier2Open(levels, records);
  // **買わなくても1面目だけは遊べる。** 第一章を通したら開く。
  const freeId = freeExtraId(levels);
  const freeOpen = isChapter1Complete(levels, records);

  const loop = options.loop ?? 1;
  const counters: Record<LevelCategory, number> = { main: 0, extra: 0 };

  return levels.map((level, levelIndex) => {
    const category = categoryOf(level.id);
    const rec = records.levels[recordIdFor(level.id, loop)];
    const cleared = rec !== undefined;

    counters[category] += 1;
    const number = counters[category];
    // 区分ごとに「その区分が解放されているか」×「その中での直前クリア」で決まる。
    // エクストラは前半・後半の2段。後半は前半を全部通すと**まとめて**開く。
    const isTier2 = category === 'extra' && tier2Ids.includes(level.id);
    const withinGroup =
      category === 'main'
        ? loop >= 2 || isLevelUnlocked(records, level.id, mainIds)
        : isTier2
          ? true // 後半は解放されたら全面まとめて選べる
          : isLevelUnlocked(records, level.id, tier1Ids);
    const groupOpen =
      category === 'main' ? true : isTier2 ? extraOpen && tier2Open : extraOpen;
    const isFreeTrial = level.id === freeId && freeOpen;
    // 体験版は本編の上限より先を閉じる。試遊の面（エクストラ1）も閉じる——
    // 体験版の中にさらに体験版があるのは分かりにくい。
    const beyondDemo =
      options.demoLimit !== undefined &&
      (category !== 'main' || number > options.demoLimit);
    const unlocked =
      options.unlockAll === true
        ? true
        : beyondDemo
          ? false
          : isFreeTrial || (groupOpen && withinGroup);

    return {
      id: level.id,
      name: level.name,
      number,
      category,
      unlocked,
      cleared,
      bestTimeMs: rec?.bestTimeMs ?? null,
      bestMoves: rec?.bestMoves ?? null,
      levelIndex,
      isExtraTier2: isTier2,
      isFreeTrial: isFreeTrial && !extraOpen,
    };
  });
}

/**
 * ある面をクリアした直後に、次へ進むべき面の添字。無ければ `null`。
 *
 * **同じ区分の中でのみ次へ進む。** 本編の最終面をクリアしたときに
 * 黙ってエクストラへ流し込むと、全面クリア画面（METAGAME-SPEC §4）を
 * 出す機会を逃してしまう。
 */
export function nextLevelIndex(
  levels: readonly Level[],
  currentIndex: number,
): number | null {
  const current = levels[currentIndex];
  if (current === undefined) return null;
  const category = categoryOf(current.id);
  for (let i = currentIndex + 1; i < levels.length; i += 1) {
    if (categoryOf(levels[i].id) === category) return i;
  }
  return null;
}

/**
 * クリア直後に自動で送ってよい次の面の添字。無ければ `null`。
 *
 * **`nextLevelIndex` だけでは足りない。** あれは「同じ区分の次」を返すだけで、
 * *解放されているか*を見ていない。エクストラの1面目は試遊で開くので、
 * そこを通水すると2面目以降へ素通りできてしまっていた——
 * **買わずにエクストラを全部遊べる**という致命的な穴だった。
 *
 * 出口はここ一箇所に寄せる。進む先が閉じているなら、そこで留まる
 * （ステージ選択に戻れば「買う」導線が出る）。
 */
export function nextPlayableIndex(
  levels: readonly Level[],
  records: RecordsData,
  currentIndex: number,
  options: StageListOptions = {},
): number | null {
  const next = nextLevelIndex(levels, currentIndex);
  if (next === null) return null;
  const entry = buildStageList(levels, records, options)[next];
  return entry !== undefined && entry.unlocked ? next : null;
}

/** 本編を全部クリアしたか。全面クリア画面を出す条件。 */
export function isMainComplete(levels: readonly Level[], records: RecordsData): boolean {
  const ids = mainLevelIds(levels);
  return ids.length > 0 && ids.every((id) => records.levels[id] !== undefined);
}

/** 本編の進捗。「7 / 10」のような表示に使う。 */
export function mainProgress(
  levels: readonly Level[],
  records: RecordsData,
): { cleared: number; total: number } {
  const ids = mainLevelIds(levels);
  return {
    cleared: ids.filter((id) => records.levels[id] !== undefined).length,
    total: ids.length,
  };
}

/** 本編の合計ベストタイム。1面でも未クリアなら `null`。 */
export function mainTotalBestTime(
  levels: readonly Level[],
  records: RecordsData,
): number | null {
  return totalBestTime(records, mainLevelIds(levels));
}

/** 全25面（本編＋エクストラ）を通したか。踏破の画面を出す条件。 */
export function isAllComplete(levels: readonly Level[], records: RecordsData): boolean {
  return levels.length > 0 && levels.every((lv) => records.levels[lv.id] !== undefined);
}

/** 全面の合計ベストタイム。1面でも未クリアなら `null`。 */
export function grandTotalBestTime(
  levels: readonly Level[],
  records: RecordsData,
): number | null {
  return totalBestTime(records, levels.map((lv) => lv.id));
}

/**
 * 踏破の称号。**速さでは変わらない。**
 *
 * `rankOf` の段位（速さ）とは役割を分けてある。段位は縮まるので誇りの置き場に
 * ならないが、踏破は一度きりで、後から失われない。だから証拠として使えるのは
 * こちらだけである。速さは段位で別に讃える。
 */
/**
 * 速さの段位の鍵。**表示する語はここに置かない。**
 * 純ロジックが言語を知らずに済むように、訳は `ui/i18n.ts` が持つ。
 */
export type RankKey = 'none' | 'sovereign' | 'architect' | 'mason' | 'apprentice' | 'wayfarer';

/**
 * 称号を持っているか。持っていればステージ選択の見出しに出し続ける。
 * 記録に日時が刻まれているかだけで決める——面の一覧が増減しても、
 * 一度得た称号が消えないようにするため。
 */
export function hasCompletionTitle(records: RecordsData): boolean {
  return records.progress.allClearedAt !== null;
}

/**
 * 合計タイムから称号を決める。METAGAME-SPEC §4 の 5。
 *
 * 閾値は「1面あたりの平均秒数」で決める。面数が増えても意味が変わらないようにするため。
 */
export function rankOf(totalMs: number, levelCount: number): RankKey {
  if (levelCount <= 0 || !Number.isFinite(totalMs)) return 'none';
  const avgSec = totalMs / levelCount / 1000;
  if (avgSec < 20) return 'sovereign';
  if (avgSec < 40) return 'architect';
  if (avgSec < 75) return 'mason';
  if (avgSec < 150) return 'apprentice';
  return 'wayfarer';
}
